import { ChangeDetectorRef, Component, ElementRef, Inject, OnInit, Optional, ViewChild, inject } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { ErrorHandlerService } from 'src/app/api/error-handler.service';
import { AutoDrawService } from 'src/app/api/routes/autodraw.service';
import { NotifyService } from 'src/app/api/routes/notify.service';
import { BaseComponent } from 'src/app/components/base.component';
import { CloseAction, OpenAction, SpinnerState } from 'src/app/store/spinner';
import { TranslateService } from '@ngx-translate/core';
import { AutoDrawModels } from 'src/app/core/models';
import { BehaviorSubject, Observable, combineLatest, switchMap, of, takeUntil, tap } from 'rxjs';
import { catchError, filter, map, take } from 'rxjs/operators';
import { AUTO_DRAW_ENUM, FILE_ENUM, NOTIFY_ENUM, EnumState } from 'src/app/core/enum/index';
import * as moment from 'moment';
import { SafeResourceUrl } from '@angular/platform-browser';
import { FileService } from 'src/app/shared/file.service';
import { environment } from "src/environments/environment";
import { NotifyModels } from 'src/app/core/models/notify';
import { CheckForm } from '../autodraw/check-form.service';
import { GeneralService } from 'src/app/shared/general.service';
import { ContactUsComponent } from '../contact-us/contact-us.component';
import { AlertService } from 'src/app/shared/alert.service';


@Component({
  selector: 'app-result',
  templateUrl: './result.component.html',
  styleUrls: ['./result.component.scss']
})
export class ResultComponent extends BaseComponent implements OnInit {
	@ViewChild('closeModalBtn', { read: ElementRef }) closeModalBtn!: ElementRef;

	// 子元件
	@ViewChild(ContactUsComponent) contactUsComponent!: ContactUsComponent;

	// 左側全選
	public selectAll: boolean = false;

    // 當前參數資料
	public project: AutoDrawModels.Project = new AutoDrawModels.Project();

	// 自動繪圖或自動報價
	public resultType = 0;

	// enums
	public ALGO_TYPE = AUTO_DRAW_ENUM.ALGO_TYPE;
	public FRAME_TYPE = AUTO_DRAW_ENUM.FRAME;
	public LANGUAGE = AUTO_DRAW_ENUM.LANGUAGE;
	public REQUEST_FROM = AUTO_DRAW_ENUM.REQUEST_FROM;
	public IS_RETURN_REPORT = AUTO_DRAW_ENUM.IS_RETURN_REPORT;
	public HARDNESS_TESTER_TOLERANCE = AUTO_DRAW_ENUM.HARDNESS_TESTER_TOLERANCE;
	public HARDNESS_TESTER = AUTO_DRAW_ENUM.HARDNESS_TESTER;
	public MEMO_LENGTH = AUTO_DRAW_ENUM.MEMO_LENGTH;
	public FILE_EXT_TYPE = FILE_ENUM.FILE_EXT_TYPE;
	public NOTIFY_METHOD = NOTIFY_ENUM.NOTIFY_METHOD;
	public FILE_NAME = AUTO_DRAW_ENUM.FILE_NAME;
	public TIM_TYPE = AUTO_DRAW_ENUM.TIM_TYPE;

	// 於environments中設定收件人信箱
	public RECEIVERS: {receiverName: string, receiverMail: string}[] = environment.mailConfig.receivers;

	// 當前自動繪圖檔案links
	public pngLinkOutside: string = '';
	public dxfLinkOutside: string = '';
	public pdfLinkOutside: string = '';
	public pngSafeLink: SafeResourceUrl = '';
	public downloadFrameSelect: number = 1;

	// 圖片大小及位置
    public imageWidth = 70;
    public dragPosition = {x:0, y:0};

	// 聯絡我們參數備註開啟狀態
	public memoStatusList: boolean[] = [];

	// 開啟中的modal
	public openingModal: string = "";

	public downloadFileType: number = this.FILE_EXT_TYPE.PNG.CODE;

	public autodrawParams: AutoDrawModels.AlgoTim[] = [];

	private enums: any;

	constructor(
		private autoDrawServ: AutoDrawService,
		private cd: ChangeDetectorRef,
		private spinnerState: Store<SpinnerState>,
		private errServ: ErrorHandlerService,
		private route: ActivatedRoute,
		private translate: TranslateService,
		private fileServ: FileService,
		private notifyServ: NotifyService,
		private checkFormServ: CheckForm,
		private generalServ: GeneralService,
		private alertServ: AlertService,
		@Optional() @Inject('APP_INITIALIZED') private appInitialized$: BehaviorSubject<boolean>
	) { super(); }

	ngOnInit(): void {
		console.log('result')
		// 先初始化 enums 為空物件
		this.enums = {} as EnumState;

		// 使用 combineLatest 來同時監聽 isLoaded$ 和 enumSubject
		combineLatest([
			AUTO_DRAW_ENUM.isLoaded$,
			AUTO_DRAW_ENUM.enumSubject
		]).pipe(
			// 只在兩個值都ready時觸發
			filter(([isLoaded, enums]) => 
				isLoaded && !!enums && Object.keys(enums).length > 0
			),
			// 只取第一次的值
			take(1),
			takeUntil(this.unsubscribe$)
		).subscribe({
			next: ([_, data]) => {
				// console.log('Initial enum data loaded:', data);
				this.enums = data;
				this.updateEnumValues();
				this.cd.detectChanges();
		
				// 等 enum 載入完成後才執行初始化
				this.initializeComponent();
		
				// 設置後續更新的監聽
				AUTO_DRAW_ENUM.enumSubject
				.pipe(
					filter(enums => !!enums && Object.keys(enums).length > 0),
					takeUntil(this.unsubscribe$)
				)
				.subscribe(updatedData => {
					// console.log('Enum data updated:', updatedData);
					this.enums = updatedData;
					this.updateEnumValues();
					this.cd.detectChanges();
				});
			},
			error: (error) => {
				console.error('Failed to initialize enums:', error);
			}
		});
	  
		// 監控 isLoaded$ 的變化（僅用於調試）
		AUTO_DRAW_ENUM.isLoaded$.pipe(
		  	takeUntil(this.unsubscribe$)
		).subscribe(isLoaded => {
		  	// console.log('isLoaded$ value changed:', isLoaded);
		});
	}

	// 新增一個方法來更新所有使用到的 enum 值
	private updateEnumValues(): void {
		if (!this.enums) return;
		try {
			this.TIM_TYPE = this.enums.TIM_TYPE;
			this.ALGO_TYPE = this.enums.ALGO_TYPE;
			this.FRAME_TYPE = this.enums.FRAME;
			this.LANGUAGE = this.enums.LANGUAGE;
			this.REQUEST_FROM = this.enums.REQUEST_FROM;
			this.IS_RETURN_REPORT = this.enums.IS_RETURN_REPORT;
			this.HARDNESS_TESTER = this.enums.HARDNESS_TESTER;
			this.HARDNESS_TESTER_TOLERANCE = this.enums.HARDNESS_TESTER_TOLERANCE;
			this.FILE_NAME = this.enums.FILE_NAME;
		} catch (error) {
			console.error('Error updating enum values:', error);
		}
	}

	// 創建一個初始化方法
	private initializeComponent = () => {
		this.getProject();
		setTimeout(() => {
			this.generalServ.initTooltips();
		}, 0);
	}

	override ngOnDestroy() {
		// 銷毀元件時關閉modal
		this.closeModal();
	}

	/**
     * 取得資料
     */
	public getProject() {
		this.resultType = parseInt(<string>sessionStorage.getItem('type'));
		const dataId = parseInt(<string>sessionStorage.getItem('id'));
		this.getRecord(this.resultType, dataId).subscribe();
	}

	/**
     * 查詢紀錄
     * @param type `number`: 出圖/報價
     * @param id `number`
     * @returns `Observable<AutoDraw.Project>`
     */
	public getRecord(type: number, id: number): Observable<number>{
		return this.autoDrawServ.get_records(id).pipe(
			catchError(err => {
				this.errServ.httpErrorHandle(err);
				return of()
			}),
			map(res => res.Data),
			map((data: AutoDrawModels.Project) => {
				const tims: AutoDrawModels.Tim[] = []
				Object.assign(this.project, data);
				// this.project = data;
				data.tims.forEach(tim => {
					let newTim = new AutoDrawModels.Tim()
					tims.push(Object.assign(newTim, tim))
				});
				this.project.tims = tims;
				// 設置聯絡我們背著開啟狀態
				this.memoStatusList = new Array(this.project.tims.length).fill(false);
				this.cd.markForCheck();
				return type
			}),
		);
	}

	/**
     * 串接繪圖演算法API
     * @returns `Observable<any>`
     */
	public algoDraw() {
		let tims: AutoDrawModels.AlgoTim[] = []
		this.project.tims.forEach(tim => {
			// 已選擇且非導熱硅脂才可產生圖紙
			if (tim.selected && tim.tim_type !== this.TIM_TYPE['TMP']){
				tims.push(this.composeAlgoParam(tim, this.ALGO_TYPE.draw));
			}
		});
		this.autodrawParams = tims;
		
		// 檢查選擇數量
		const MAX_PARAM_LIMIT = 8;      // 目前最多一次8筆
		if (!tims.length) {
			this.alertServ.openAlert(this.translate.instant("AUTO_DRAW.ALERT.PARAMS_EMPTY"));
			return
		} else if (tims.length > MAX_PARAM_LIMIT) {
			this.alertServ.openAlert(this.translate.instant("AUTO_DRAW.ALERT.PARAMS_OVER", {
				max: MAX_PARAM_LIMIT
			}));
			return
		}

		const param = new AutoDrawModels.AlgoParams(
			this.FRAME_TYPE.outside, 
			this.LANGUAGE.tc, 
			this.REQUEST_FROM.asink, 
			tims
		);
		
		this.spinnerState.dispatch(new OpenAction('Loading...'));
		this.autoDrawServ.get_draw(param).pipe(
			catchError(err => {
				this.spinnerState.dispatch(new CloseAction());
				this.errServ.httpErrorHandle(err);
				return of()
			}),
			map(res => res.Data),
			tap((data: AutoDrawModels.DrawResponse) => {
				// 將base64檔案都轉為link
				const fileLinks = {
					png: this.base64ToLink(data.outside_png, 'IMG'),
					dxf: this.base64ToLink(data.outside_dxf, 'IMG'),
					pdf: this.base64ToLink(data.outside_pdf, 'PDF'),
				}
				this.pngLinkOutside = fileLinks.png;
				this.dxfLinkOutside = fileLinks.dxf;
				this.pdfLinkOutside = fileLinks.pdf;
				const showImg = this.pngLinkOutside || "";
				this.pngSafeLink = this.fileServ.linkToSelfUrl(showImg);

				this.resultType = this.ALGO_TYPE.draw;
				this.cd.markForCheck();
				this.spinnerState.dispatch(new CloseAction());
			})
		).subscribe();
	}

	/**
     * 將base64轉為link
     * @param base64 `string | null`: base64
     * @param fileType `string`: 檔案類型
     * @returns `string`: link(若無則為空值)
     */
	public base64ToLink(base64: string | null, fileType: string): string {
		if (base64) {
			const buffer = this.fileServ.base64ToArraybuffer(base64);
			return this.fileServ.getFileLink(buffer, fileType);
		}

		return "";
	}


	/**
	 * 提交聯絡我們表單
	 */
	public submitContact() {
		const hasCompanyName = this.checkNull(this.project.company_name);
		const hasApplicantName = this.checkNull(this.project.applicant_name);
		const hasApplicant = this.checkNull(this.project.applicant_email);
		const hasApplicantPhone = this.checkNull(this.project.applicant_phone);
		const hasNull = !(hasCompanyName && hasApplicantName && hasApplicant && hasApplicantPhone);
		const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
		const invalidRegex = !emailRegex.test(<string>this.project.applicant_email);
		const invalidMemo = this.project.tims.some(tim => this.checkFormServ.checkMemoLength(tim, this.MEMO_LENGTH.MAX));
		if (hasNull) return alert(this.translate.instant("AUTO_DRAW.ALERT.NULL"));
		else if (invalidRegex) return alert(this.translate.instant("AUTO_DRAW.ALERT.EMAIL"));
		else if (invalidMemo) return alert(this.translate.instant("AUTO_DRAW.ALERT.MEMO", {max: this.MEMO_LENGTH.MAX}))
		let param = this.project;
		this.spinnerState.dispatch(new OpenAction('Loading...'));
        this.autoDrawServ.update_records(param).pipe(
            catchError(err => {
                this.spinnerState.dispatch(new CloseAction());
                this.errServ.httpErrorHandle(err);
                return of()
            }),
            map(res => res.Data),
			switchMap(data => this.createQueue())
        ).subscribe();
	}

	/**
	 * 串接API建立Notify Queue
	 * @returns `Observable<any>`
	 */
	public createQueue(): Observable<any> {
		// 組成queue參數
		const testTitle = environment.production ? '' : '(test)';	// 若為測試環境則用加上(test)
		const params: NotifyModels.QueueParam[] = [];
		let paramsContent = '';
		this.project.tims.forEach(tim => paramsContent += tim.composeParamsContent(this.translate));
		this.RECEIVERS.forEach(receiver => {
			const content = this.project.composeMailContent(receiver.receiverName, paramsContent);
			let param = {
				title: this.translate.instant('NOTIFY.CONTACT.TITLE', {test: testTitle}),
				notify_method: this.NOTIFY_METHOD.ASINK_AUTO_DRAW_CONTACT_US,
				sender_name: this.project.applicant_name || '',
				receiver_name: receiver.receiverName,
				receiver_mail: receiver.receiverMail,
				template_id: 1,		// 無影響，使用content內容
				content: content
			}
			params.push(param);
		});
		return this.notifyServ.create_records(params).pipe(
            catchError(err => {
                this.spinnerState.dispatch(new CloseAction());
                this.errServ.httpErrorHandle(err);
                return of()
            }),
            map(res => res.Data),
            tap((data) => {
                this.spinnerState.dispatch(new CloseAction());
				alert(this.translate.instant('AUTO_DRAW.ALERT.CONTACT'))
				this.closeModal();
            })
		);
	}

	/**
	 * 檢查字串是否為空值
	 * @param str `string | null`
	 * @returns `boolean` true 非空值, false 空值
	 */
	public checkNull(str: string | null): boolean {
		const processStr = str ? str.trim() : "";
		const result = processStr.length > 0;
		return result;
	}

	/**
     * 全選變動改變所有項目勾選
     * @param $event `boolean`
     */
	public toggleSelectAll($event: boolean){
		this.project.tims.forEach(tim => {
			tim.selected = this.selectAll;
		});
	}

	/**
     * 勾選項目時檢查是否全選，改變全選狀態
     */
	public changeSelectAll(){
		const listHasFalse = this.project.tims.some(tim => {
			// 導熱硅脂無法勾選產生圖紙
			return tim.tim_type === this.TIM_TYPE['TMP'] ? false : !tim.selected
		});
		
		this.selectAll = !listHasFalse;
	}

	/**
     * 組成演算法參數
     * @param tim `AutoDraw.Tim`
     * @returns `AutoDraw.AlgoParams`
     */
	public composeAlgoParam(tim: AutoDrawModels.Tim, algoType: number): AutoDrawModels.AlgoTim{
        const d1 = tim.length;
        const d2 = tim.width
		const timDate = moment(tim.date).utc().format('YYYYMMDD')
		const param = new AutoDrawModels.AlgoTim(
			tim.id,
			tim.file_name,
			tim.number,
			tim.tim_type,
			tim.tim_model,
			tim.drawing_number,
			tim.ship,
			tim.thermal_coefficient,
			// 繪圖: d1*d2, 報價: d1*d2
			//長為d1
			algoType === this.ALGO_TYPE.draw? d1 : d1,  //其實可以直接寫d1，怕後來要改先留，確認沒問題再刪
			// 寬為d2
            algoType === this.ALGO_TYPE.draw? d2 : d2,  //其實可以直接寫d２，怕後來要改先留，確認沒問題再刪
			tim.thickness,
			tim.layer,
			tim.flat_length,
			tim.flat_width,
			tim.pulltab_length,
			tim.hardness,
			tim.hardness_tolerance,		// 公規固定用shcore C
			tim.hardness_tester,				// 公規固定用shcore C
			tim.color,
			tim.adhesive,
			tim.adhesive_type,
			tim.adhesive_include_thickness,
			tim.adhesive_thickness,
			tim.quantity,
			tim.view_angle,
			this.project.company_name,
			this.project.applicant_name,
			timDate,
			tim.trim_hole,
			tim.slide_cut,
			this.IS_RETURN_REPORT.YES        // 是否回傳excel
		);

		return param
	}

	/**
     * 根據所選擇檔案類型下載
	 * TODO: 檔案名稱後續需與圖號相同
     */
	public downloadFile(type: number) {
		
		const nowStr = moment().format('YYYYMMDD');
		// 組成檔案名稱
		const firstParam = this.autodrawParams[0];
		const tim_type = firstParam.tim_type;
		const timTypeIsSame = this.autodrawParams.every(param => param.tim_type === tim_type);
		let fileNameStr = '';
		if (!timTypeIsSame) {
			fileNameStr = this.FILE_NAME.MIX;
		} else {
			fileNameStr = tim_type === this.TIM_TYPE['TIM'] ? this.FILE_NAME.TIM : this.FILE_NAME.TMP;
		}

		fileNameStr = `${fileNameStr}_${firstParam.length}_${firstParam.width}_${firstParam.thickness}_TIM${nowStr}`
		switch (type) {
			case this.FILE_EXT_TYPE.PNG.CODE:
				let pngLink = this.pngLinkOutside;
				this.fileServ.downloadLink(pngLink, `${fileNameStr}.${this.FILE_EXT_TYPE.PNG.EXT}`);
				break;
			case this.FILE_EXT_TYPE.PDF.CODE:
				let pdfLink = this.pdfLinkOutside;
				this.fileServ.downloadLink(pdfLink, `${fileNameStr}.${this.FILE_EXT_TYPE.PDF.EXT}`);
				break;
		}
	}

	/**
     * 調整工作區中圖片大小及位置
     * @param type `string`: `BIG` + 5, `SMALL` - 5, `RESET` 重置
     */
	public changeImgStatus(type: string){
		switch (type) {
			case 'BIG':
				this.imageWidth += 5
				break;
			case 'SMALL':
				this.imageWidth -= this.imageWidth - 5 > 0 ? 5 : 0
				break;
			default:
				this.dragPosition = {x:0, y:0};
				this.imageWidth = 70;
				break;
		}
	}


	/**
	 * 開啟的modal類型
	 * @param type `string`: `DOWNLOAD`下載, `CONTACT`聯絡我們
	 */
	public openModal(type: string) {
		this.openingModal = type;
	}

	/**
	 * 關閉Modal
	 */
	public closeModal() {
		this.unsubscribe$.next();
		this.unsubscribe$.complete();
		if ( this.closeModalBtn !== undefined ) {
			this.closeModalBtn.nativeElement.click();
		}
	}
}
